import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
  IconButton,
  Stack,
  Grid,
  Chip,
  Container
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UndoIcon from '@mui/icons-material/Undo';
import { format } from 'date-fns';
import { getMonthlyInventoryState, recalculateMonthlyInventoryState, ProductCOGS, MonthlyInventoryState } from '../firebase/cogsServices';
import { formatCurrency } from '../utils/formatters';
import { useSnackbar } from 'notistack';
import { generateCogsGLEntryPreviews, createCogsGLTransaction, reverseCogsGLTransaction, GLEntryPreview } from '../services/glCogsService';
import GLCogsEntryPreviewDialog from '../components/GLCogsEntryPreviewDialog';
import ReverseCogsGLDialog from '../components/ReverseCogsGLDialog';

const InventoryCOGS: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [recalculating, setRecalculating] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [monthlyState, setMonthlyState] = useState<MonthlyInventoryState | null>(null);
  const [filteredProducts, setFilteredProducts] = useState<ProductCOGS[]>([]);
  
  // GL Entry preview state
  const [glEntryPreviewOpen, setGlEntryPreviewOpen] = useState<boolean>(false);
  const [glEntryPreviews, setGlEntryPreviews] = useState<GLEntryPreview[]>([]);
  const [isSubmittingGLEntries, setIsSubmittingGLEntries] = useState<boolean>(false);
  
  // GL Transaction reversal state
  const [reverseDialogOpen, setReverseDialogOpen] = useState<boolean>(false);
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Years range for the dropdown
  const years = [2023, 2024, 2025, 2026, 2027];
  
  // Months for the dropdown
  const months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' }
  ];
  
  // Fetch data when year or month changes
  useEffect(() => {
    fetchData();
    
    // Cleanup function to handle component unmounting
    return () => {
      setLoading(false);
      setRecalculating(false);
    };
  }, [selectedYear, selectedMonth]);
  
  // Filter products based on search query
  useEffect(() => {
    if (!monthlyState) return;
    
    if (!searchQuery.trim()) {
      setFilteredProducts(monthlyState.products);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = monthlyState.products.filter(product => 
      product.productName.toLowerCase().includes(query) || 
      product.productSku.toLowerCase().includes(query)
    );
    
    setFilteredProducts(filtered);
  }, [searchQuery, monthlyState]);
  
  // Fetch monthly inventory state data
  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getMonthlyInventoryState(selectedYear, selectedMonth);
      setMonthlyState(data);
      setFilteredProducts(data.products || []);
    } catch (error) {
      console.error('Error fetching COGS data:', error);
      // Reset states on error
      setMonthlyState(null);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle year change
  const handleYearChange = (event: any) => {
    setSelectedYear(Number(event.target.value));
  };
  
  // Handle month change
  const handleMonthChange = (event: any) => {
    setSelectedMonth(Number(event.target.value));
  };
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Handle refresh button click
  const handleRefresh = async () => {
    if (recalculating) return;
    setRecalculating(true);
    
    try {
      const state = await recalculateMonthlyInventoryState(selectedYear, selectedMonth);
      setMonthlyState(state);
      setFilteredProducts(state.products);
      enqueueSnackbar('COGS data refreshed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error refreshing COGS data:', error);
      enqueueSnackbar('Error refreshing COGS data', { variant: 'error' });
    } finally {
      setRecalculating(false);
    }
  };
  
  // Handle GL entry preview button click
  const handleGLEntryPreviewClick = () => {
    if (!monthlyState) return;
    
    // Generate GL entry previews
    const previews = generateCogsGLEntryPreviews(monthlyState);
    setGlEntryPreviews(previews);
    setGlEntryPreviewOpen(true);
  };
  
  // Handle GL entry posting
  const handlePostGLEntries = async () => {
    if (!monthlyState) return;
    setIsSubmittingGLEntries(true);
    
    try {
      await createCogsGLTransaction(monthlyState, glEntryPreviews);
      
      // Refresh the data to show updated GL posting status
      const updatedState = await getMonthlyInventoryState(selectedYear, selectedMonth);
      setMonthlyState(updatedState);
      setFilteredProducts(updatedState.products);
      
      enqueueSnackbar('GL entries posted successfully', { variant: 'success' });
      setGlEntryPreviewOpen(false);
    } catch (error) {
      console.error('Error posting GL entries:', error);
      enqueueSnackbar(`Error posting GL entries: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsSubmittingGLEntries(false);
    }
  };
  
  // Handle reverse GL transaction button click
  const handleReverseGLClick = () => {
    setReverseDialogOpen(true);
  };
  
  // Handle GL transaction reversal
  const handleReverseGLTransaction = async () => {
    if (!monthlyState || !monthlyState.id) {
      throw new Error('Monthly inventory state not found');
    }
    
    await reverseCogsGLTransaction(monthlyState.id);
    
    // Refresh the data to show updated GL posting status
    const updatedState = await getMonthlyInventoryState(selectedYear, selectedMonth);
    setMonthlyState(updatedState);
    setFilteredProducts(updatedState.products);
    
    enqueueSnackbar('GL transaction reversed successfully', { variant: 'success' });
  };
  
  // Export data to CSV
  const handleExport = () => {
    if (!monthlyState) return;
    
    // Create CSV content
    const headers = [
      'Product Name',
      'SKU',
      'Beginning Stock',
      'Beginning Value',
      'Purchase Quantity',
      'Purchase Value',
      'Manual Additions',
      'Manual Reductions',
      'Sales Quantity',
      'Ending Stock',
      'Ending Value',
      'Average Cost',
      'COGS'
    ];
    
    const rows = monthlyState.products.map(product => [
      product.productName,
      product.productSku,
      product.beginningStock,
      product.beginningValue.toFixed(2),
      product.purchaseQuantity,
      product.purchaseValue.toFixed(2),
      product.manualAdditions,
      product.manualReductions,
      product.salesQuantity,
      product.endingStock,
      product.endingValue.toFixed(2),
      product.averageCost.toFixed(2),
      product.totalCOGS.toFixed(2)
    ]);
    
    // Add totals row
    rows.push([
      'TOTALS',
      '',
      monthlyState.products.reduce((sum, p) => sum + p.beginningStock, 0),
      monthlyState.totals.beginningValue.toFixed(2),
      monthlyState.products.reduce((sum, p) => sum + p.purchaseQuantity, 0),
      monthlyState.totals.purchaseValue.toFixed(2),
      monthlyState.products.reduce((sum, p) => sum + p.manualAdditions, 0),
      monthlyState.products.reduce((sum, p) => sum + p.manualReductions, 0),
      monthlyState.products.reduce((sum, p) => sum + p.salesQuantity, 0),
      monthlyState.products.reduce((sum, p) => sum + p.endingStock, 0),
      monthlyState.totals.endingValue.toFixed(2),
      '',
      monthlyState.totals.totalCOGS.toFixed(2)
    ]);
    
    // Convert to CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory-cogs-${selectedYear}-${selectedMonth + 1}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Inventory COGS
      </Typography>
      
      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ width: { xs: '100%', sm: '25%', md: '16.66%' } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={handleYearChange}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ width: { xs: '100%', sm: '25%', md: '16.66%' } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={handleMonthChange}
              >
                {months.map(month => (
                  <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ width: { xs: '100%', sm: '33.33%', md: '25%' } }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              startIcon={<RefreshIcon />}
              variant="outlined"
              onClick={handleRefresh}
              disabled={recalculating}
            >
              {recalculating ? 'Recalculating...' : 'Recalculate'}
            </Button>
            <Button
              startIcon={<FileDownloadIcon />}
              variant="outlined"
              onClick={handleExport}
            >
              Export CSV
            </Button>
            
            {/* GL Entry Posting Button */}
            {monthlyState && !monthlyState.glTransactionId && (
              <Button
                startIcon={<AccountBalanceIcon />}
                variant="outlined"
                color="primary"
                onClick={handleGLEntryPreviewClick}
              >
                Post GL Entries
              </Button>
            )}
            
            {/* Reverse GL Transaction Button */}
            {monthlyState && monthlyState.glTransactionId && (
              <Button
                startIcon={<UndoIcon />}
                variant="outlined"
                color="error"
                onClick={handleReverseGLClick}
              >
                Reverse GL
              </Button>
            )}
            
            {/* GL Posting Status */}
            {monthlyState && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                {monthlyState.glTransactionId ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="GL Posted"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    icon={<InfoIcon />}
                    label="GL Not Posted"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </Paper>
      
      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4, flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading inventory data...
          </Typography>
        </Box>
      )}
      
      {/* No data message */}
      {!loading && (!monthlyState || !filteredProducts.length) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No inventory data available for this period
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            {monthlyState && filteredProducts.length === 0 && searchQuery ? 
              'No products match your search criteria.' : 
              'You can calculate the inventory data for this period.'}
          </Typography>
          <Button 
            variant="contained" 
            sx={{ mt: 2 }}
            onClick={handleRefresh}
            disabled={recalculating}
          >
            {recalculating ? 'Calculating...' : 'Calculate Now'}
          </Button>
        </Paper>
      )}
      
      {/* Summary Cards */}
      {monthlyState && filteredProducts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Beginning Inventory
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(monthlyState.totals.beginningValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filteredProducts.reduce((sum, p) => sum + p.beginningStock, 0).toFixed(0)} units
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Purchases Value
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(monthlyState.totals.purchaseValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ color: 'success.main' }}>
                    +{filteredProducts.reduce((sum, p) => sum + p.purchaseQuantity, 0).toFixed(0)} units
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ending Inventory
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(monthlyState.totals.endingValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filteredProducts.reduce((sum, p) => sum + p.endingStock, 0).toFixed(0)} units
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Total COGS
                    <Tooltip title="Cost of Goods Sold = (Beginning Inventory + Purchases - Ending Inventory)">
                      <IconButton size="small" sx={{ p: 0.25 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Typography>
                  <Typography variant="h5" color="primary.main">
                    {formatCurrency(monthlyState.totals.totalCOGS)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ color: 'error.main' }}>
                    -{filteredProducts.reduce((sum, p) => sum + p.salesQuantity, 0).toFixed(0)} units sold
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Main Data Table */}
      {monthlyState && filteredProducts.length > 0 && (
        <Paper>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Beginning Stock</TableCell>
                  <TableCell align="right">Beginning Value</TableCell>
                  <TableCell align="right">Purchases</TableCell>
                  <TableCell align="right">Purchase Value</TableCell>
                  <TableCell align="right">Manual Additions</TableCell>
                  <TableCell align="right">Manual Reductions</TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Ending Stock</TableCell>
                  <TableCell align="right">Ending Value</TableCell>
                  <TableCell align="right">Avg. Cost</TableCell>
                  <TableCell align="right">Write-off Cost</TableCell>
                  <TableCell align="right">COGS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.productId} hover>
                    <TableCell>{product.productName}</TableCell>
                    <TableCell>{product.productSku}</TableCell>
                    <TableCell align="right">{product.beginningStock.toFixed(2)}</TableCell>
                    <TableCell align="right">{formatCurrency(product.beginningValue)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {product.purchaseQuantity > 0 ? `+${product.purchaseQuantity.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {formatCurrency(product.purchaseValue)}
                        {product.hasUnpostedGLTransactions && (
                          <Tooltip title="Has unposted GL transactions">
                            <InfoIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                        {product.purchaseQuantity > 0 && !product.hasUnpostedGLTransactions && (
                          <Tooltip title="All GL transactions posted">
                            <CheckCircleIcon fontSize="small" color="success" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {product.manualAdditions > 0 ? `+${product.manualAdditions.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {product.manualReductions > 0 ? `-${product.manualReductions.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {product.salesQuantity > 0 ? `-${product.salesQuantity.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right">{product.endingStock.toFixed(2)}</TableCell>
                    <TableCell align="right">{formatCurrency(product.endingValue)}</TableCell>
                    <TableCell align="right">{formatCurrency(product.averageCost)}</TableCell>
                    <TableCell align="right">{formatCurrency(product.writeOffCost || 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(product.totalCOGS)}</TableCell>
                  </TableRow>
                ))}
                
                {/* Totals Row */}
                {monthlyState && (
                  <TableRow sx={{ '& td': { fontWeight: 'bold', backgroundColor: '#f5f5f5' } }}>
                    <TableCell colSpan={2}>TOTALS</TableCell>
                    <TableCell align="right">
                      {filteredProducts.reduce((sum, p) => sum + p.beginningStock, 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(filteredProducts.reduce((sum, p) => sum + p.beginningValue, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {`+${filteredProducts.reduce((sum, p) => sum + p.purchaseQuantity, 0).toFixed(2)}`}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(filteredProducts.reduce((sum, p) => sum + p.purchaseValue, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {`+${filteredProducts.reduce((sum, p) => sum + p.manualAdditions, 0).toFixed(2)}`}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {`-${filteredProducts.reduce((sum, p) => sum + p.manualReductions, 0).toFixed(2)}`}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {`-${filteredProducts.reduce((sum, p) => sum + p.salesQuantity, 0).toFixed(2)}`}
                    </TableCell>
                    <TableCell align="right">
                      {filteredProducts.reduce((sum, p) => sum + p.endingStock, 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(filteredProducts.reduce((sum, p) => sum + p.endingValue, 0))}
                    </TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell align="right">
                      {formatCurrency(filteredProducts.reduce((sum, p) => sum + (p.writeOffCost || 0), 0))}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(filteredProducts.reduce((sum, p) => sum + p.totalCOGS, 0))}
                    </TableCell>
                  </TableRow>
                )}
                
                {filteredProducts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      No products found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      
      {/* Explanation Section */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          How COGS is Calculated
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" paragraph>
          <strong>Average Cost Calculation:</strong> The average cost is calculated at month-end by dividing the total inventory value (beginning value + purchase value) by the total units (beginning stock + purchases + net manual adjustments).
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>COGS Calculation:</strong> Cost of Goods Sold is calculated by multiplying the sales quantity by the average cost.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>GL Posting:</strong> When GL entries are posted, the following accounts are updated:
          <ul>
            <li>Debit to 8100001 (COGS - Store) for sales costs</li>
            <li>Debit to 7300001 (Inventory Write Off) for write-off costs</li>
            <li>Credit to 1300002 (Inventory Balances) for the total amount</li>
          </ul>
        </Typography>
        <Typography variant="body2">
          <strong>Note:</strong> Manual additions with zero cost (free products) will dilute the average cost, as they increase the quantity but not the value.
        </Typography>
      </Paper>
      
      {/* GL Entry Preview Dialog */}
      {monthlyState && (
        <GLCogsEntryPreviewDialog
          open={glEntryPreviewOpen}
          onClose={() => setGlEntryPreviewOpen(false)}
          onConfirm={handlePostGLEntries}
          entries={glEntryPreviews}
          isSubmitting={isSubmittingGLEntries}
          year={selectedYear}
          month={selectedMonth}
          totalCOGS={monthlyState.products.reduce((sum, p) => sum + p.totalCOGS, 0)}
          totalWriteOffs={monthlyState.products.reduce((sum, p) => sum + (p.writeOffCost || 0), 0)}
        />
      )}
      
      {/* Reverse GL Dialog */}
      {monthlyState && (
        <ReverseCogsGLDialog
          open={reverseDialogOpen}
          onClose={() => setReverseDialogOpen(false)}
          onConfirm={handleReverseGLTransaction}
          year={selectedYear}
          month={selectedMonth}
        />
      )}
    </Box>
  );
};

export default InventoryCOGS;
